import { Injectable } from '@angular/core';
import { SwPush, SwUpdate } from '@angular/service-worker';
import { HttpClient } from '@angular/common/http';
import { TokenStorageService } from './token-storage.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  readonly VAPID_PUBLIC_KEY = 'BC1sdKS11jm-D_jv0bDMssajEgf4d3nEKYEqerBSXdIPbZhxyhdl0GLnc_DSD7lb6D5EQordkNEnCeN8alzul9Y';
  private deferredPrompt: any = null;

  constructor(
    private _swPush: SwPush,
    private _swUpdate: SwUpdate,
    private _http: HttpClient,
    private _tokenStorage: TokenStorageService) {
      this.checkForUpdate();

      window.addEventListener('beforeinstallprompt', (event: Event) => {
        event.preventDefault();
        this.deferredPrompt = event;
      });
  }

  private checkForUpdate() {
    if (this._swUpdate.isEnabled) {
      this._swUpdate.versionUpdates.subscribe(event => {
      if (event.type === 'VERSION_READY') {
        // Optionally show a toast or confirmation dialog
        window.location.reload();
      }
    });
    }
  }

  async subscribeToPush(accessToken: string): Promise<void> {
    if (!accessToken) {
      throw new Error('🚫 Access Token not provided.');
    }

    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      throw new Error('Notification permission not granted.');
    }
    console.log("[Subscribe]: permission granted");

    const subscription = await this._swPush.requestSubscription({
      serverPublicKey: this.VAPID_PUBLIC_KEY
    });
    console.log("[Subscribe]: Request Subscription success");

    if(subscription){
      console.log("[Subscribe]: Sending subscription to API...");
      await this.sendSubscription(subscription, accessToken);
    }

    console.log('✅ Push subscription complete.');
  }

  async silentSubscribeToPush(accessToken: string): Promise<void> {
    try{
      if (!accessToken) {
        throw new Error('🚫 Access Token not provided.');
      }

      const subscription = await this.getCurrentSubscription();

      if(subscription){
        await this.sendSubscription(subscription, accessToken);
      }

      console.log('✅ Subscription sent to API.');
    } catch (error){
      console.error('🚫 Unable to send Subscription to API.', error);
    }
  }

  async getCurrentSubscription(): Promise<PushSubscription | null> {
    try {
      const registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('SW ready timeout')), 5000))
      ]);

      const subscription = await registration.pushManager.getSubscription();
      return subscription;
    } catch (error) {
      console.error('Error getting subscription:', error);
      return null;
    }
  }

  async getCurrentSubscriptionSW(): Promise<PushSubscription | null> {
    try {
      const subscription = await firstValueFrom(this._swPush.subscription);

      if (subscription) {
        console.log("Subscription:", subscription);
        console.log("Endpoint:", subscription.endpoint);
        console.log("Keys:", subscription.toJSON().keys);
        return subscription;
      } else {
        console.log("No subscription found.");
        return null;
      }
    } catch (err) {
      console.error('Error getting subscription:', err);
      return null;
    }
  }

  async unsubscribeFromPush(): Promise<boolean> {
    try {
      const subscription = await this.getCurrentSubscription();

      if (subscription) {
        console.log("Subscription found");
        const success = await subscription.unsubscribe();
        
        if (success) {
          console.log('🗑️ Unsubscribed successfully from push notifications.');

          //Since the service worker is registered to the domain,
          //By user request to unsubscribe, we search for all the available conversations in local storage,
          //Then notify backend to remove the subscription with each access token
          const tokens = this._tokenStorage.getAllStorageConversations();
          tokens.forEach(async item => {
            if(item.details && item.details.accessToken){
              await firstValueFrom(this._http.post(
                `${environment.apiUrl}/push/unsubscribe`, null,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'RoarChatAuth': item.details.accessToken
                  }
                }
              ));
            }
          });

          return true;
        } else {
          console.warn('⚠️ Unsubscribe call returned false. Subscription may still be active.');
        }
      } else {
        console.log('ℹ️ No active push subscription found.');
      }
      return false;
    } catch (err) {
      console.error('❌ Error during push unsubscribe:', err);
      return false;
    }
  }

  async sendSubscription(subscription: PushSubscription, accessToken: string) {
    return await firstValueFrom(
      this._http.post(
        `${environment.apiUrl}/push/subscribe`,
        subscription,
        {
          headers: {
            'Content-Type': 'application/json',
            'RoarChatAuth': accessToken
          }
        }
      )
    );
  }

  async isSubscribed(): Promise<boolean> {
      return !!(await this.getCurrentSubscription());
  }

  canPrompt(): boolean {
    return this.deferredPrompt !== null;
  }

  async promptInstallPWA(): Promise<boolean> {
    //check install availability
    if (!this.deferredPrompt) return false;
    
    this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;
    this.deferredPrompt = null;
    return outcome === 'accepted';
  }

  isPushSupported(): boolean {
    const hasPush = 'serviceWorker' in navigator && 'PushManager' in window;
    const isIOS = this.isIOS();
    const isStandalone = this.isStandalone();

    if (isIOS) {
      // On iOS, require standalone PWA mode
      return hasPush && isStandalone;
    }
    return hasPush;
  }

  isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  }

  isStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  }

  isIosNotPWA(): boolean {
    return this.isIOS() && !this.isStandalone();
  }

  isIosPWA(): boolean {
    return this.isIOS() && this.isStandalone();
  }

  isIosSafari(): boolean {
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(window.navigator.userAgent);

    return this.isIOS() && isSafari;
  }
}